import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import FlavorService from '../../services/FlavorService';
import Swal from 'sweetalert2';

const FlavorModal = ({ show, onHide, flavor, categories, onSuccess }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (flavor) {
            setName(flavor.name);
            setDescription(flavor.description || '');
            setCategoryId(flavor.flavorCategoryId.toString());
        } else {
            setName('');
            setDescription('');
            setCategoryId('');
        }
        setErrors({});
    }, [flavor, show]);

    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) {
            newErrors.name = 'El nombre es obligatorio';
        } else if (name.length > 100) {
            newErrors.name = 'El nombre no puede tener más de 100 caracteres';
        }

        if (description && description.length > 255) {
            newErrors.description = 'La descripción no puede tener más de 255 caracteres';
        }

        if (!categoryId) {
            newErrors.categoryId = 'Debe seleccionar una categoría';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const flavorData = {
                name,
                description: description || null,
                flavorCategoryId: parseInt(categoryId),
                status: true
            };

            if (flavor) {
                await FlavorService.updateFlavor(flavor.id, {
                    ...flavorData,
                    id: flavor.id
                });
            } else {
                await FlavorService.createFlavor(flavorData);
            }

            Swal.fire({
                icon: 'success',
                title: 'Éxito!',
                text: 'Gusto ' + (flavor ? 'actualizado' : 'creado') + ' correctamente',
                timer: 1500,
                showConfirmButton: false,
            });

            onSuccess();
            onHide();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.errorMsg || 'Error al ' + (flavor ? 'actualizar' : 'crear') + ' el gusto',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {flavor ? 'Editar' : 'Nuevo'} Gusto
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Nombre</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ingrese el nombre del gusto"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.name}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Descripción</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Ingrese una descripción (opcional)"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            isInvalid={!!errors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.description}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Categoría</Form.Label>
                        <Form.Select
                            value={categoryId}
                            onChange={(e) => setCategoryId(e.target.value)}
                            isInvalid={!!errors.categoryId}
                        >
                            <option value="">Seleccione una categoría</option>
                            {categories.map((category) => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errors.categoryId}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

FlavorModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    flavor: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        description: PropTypes.string,
        flavorCategoryId: PropTypes.number,
        status: PropTypes.bool
    }),
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired
        })
    ).isRequired,
    onSuccess: PropTypes.func.isRequired
};

export default FlavorModal;